import { motion } from 'framer-motion'
import { ArrowRight, KeyRound, RefreshCw, Server, Waypoints } from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { XendrelMark } from '@/components/brand/XendrelMark'
import { Badge } from '@/components/ui/badge'
import { Card } from '@/components/ui/card'
import type { RegisterSource } from '@/services/api/registers'
import { cn } from '@/utils/cn'

export interface RegisterSourceCardProps {
  source: RegisterSource
  /** Collection names the snapshot is read from, for the register node's chips. */
  collections: string[]
  className?: string
}

const REVEAL = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0 },
}

/**
 * Where the register snapshot is read from — the hosted Xendrel service, or this
 * deployment's own Typesense.
 *
 * Drawn as the wiring rather than described in a paragraph: the question an
 * analyst actually has is topological ("whose data am I searching, and what of
 * mine went there?"), and the honest answer is two boxes and an arrow. In hosted
 * mode the boxes are separate and only reads cross the link; in local mode there
 * is one box, because the registers and the case data share an instance.
 *
 * The address is configuration, not a secret (host/port/protocol live in
 * `api.env`, the search key in a secret), so it is safe to name here — see
 * `docs/extensions/registers.md`.
 */
export function RegisterSourceCard({ source, collections, className }: RegisterSourceCardProps) {
  const { t } = useTranslation()
  const hosted = source.mode === 'hosted'
  const key = `extensions.osint.sections.source.${source.mode}` as const

  return (
    <Card className={cn('relative overflow-hidden p-5', className)}>
      <div
        aria-hidden
        className="pointer-events-none absolute -right-20 -top-24 size-72 rounded-full bg-primary/5 blur-3xl"
      />
      <header className="relative flex flex-wrap items-start justify-between gap-x-6 gap-y-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 font-mono text-2xs font-semibold uppercase tracking-[0.18em] text-primary-strong">
            <Waypoints className="size-3.5" />
            {t('extensions.osint.sections.source.kicker')}
          </div>
          <h2 className="mt-1 text-base font-semibold text-foreground">{t(`${key}.title`)}</h2>
          <p className="mt-1 max-w-2xl text-xs leading-relaxed text-muted-foreground">
            {t(`${key}.lede`)}
          </p>
        </div>
        <Badge variant={hosted ? 'info' : 'muted'} className="shrink-0 gap-1.5">
          {hosted ? <XendrelMark className="size-3" /> : <Server className="size-3" />}
          {t(`${key}.badge`)}
        </Badge>
      </header>

      <motion.div
        initial="hidden"
        animate="show"
        variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }}
        className="relative mt-5 flex flex-col items-stretch gap-3 sm:flex-row"
      >
        <SourceNode
          title={t('extensions.osint.sections.source.deployment.title')}
          endpoint={hosted ? undefined : source.endpoint}
          chips={[
            t('extensions.osint.sections.source.deployment.documents'),
            t('extensions.osint.sections.source.deployment.chunks'),
            t('extensions.osint.sections.source.deployment.graph'),
            ...(hosted ? [] : collections),
          ]}
          note={t(`${key}.deploymentNote`)}
        />
        {hosted && (
          <>
            <Connector label={t('extensions.osint.sections.source.link')} />
            <SourceNode
              accent
              title={t('extensions.osint.sections.source.service.title')}
              endpoint={source.endpoint}
              chips={collections}
              note={t(`${key}.serviceNote`)}
            />
          </>
        )}
      </motion.div>

      <dl className="relative mt-5 grid gap-x-6 gap-y-3 border-t border-border/60 pt-4 sm:grid-cols-2">
        <Fact
          icon={<KeyRound className="size-3.5" />}
          term={t('extensions.osint.sections.source.access')}
          detail={t(`${key}.accessValue`)}
        />
        <Fact
          icon={<RefreshCw className="size-3.5" />}
          term={t('extensions.osint.sections.source.refresh')}
          detail={t(`${key}.refreshValue`)}
        />
      </dl>
    </Card>
  )
}

/** One box in the wiring figure: who holds what, and at which address. */
function SourceNode({
  title,
  endpoint,
  chips,
  note,
  accent = false,
}: {
  title: string
  endpoint?: string
  chips: string[]
  note: string
  /** The remote service — drawn as the emphasised half of the pair, under its own mark. */
  accent?: boolean
}) {
  return (
    <motion.div
      variants={REVEAL}
      className={cn(
        'flex-1 rounded-lg border p-3.5',
        accent ? 'border-primary/40 bg-primary/5' : 'border-border bg-muted/30'
      )}
    >
      <div className="flex items-center gap-2">
        {accent ? (
          <XendrelMark className="size-3.5 text-primary" />
        ) : (
          <Server className="size-3.5 text-muted-foreground" />
        )}
        <span className="truncate text-xs font-semibold text-foreground">{title}</span>
      </div>
      {endpoint && (
        <div className="mt-1.5 truncate font-mono text-2xs text-muted-foreground" title={endpoint}>
          {endpoint}
        </div>
      )}
      <div className="mt-2.5 flex flex-wrap gap-1">
        {chips.map(chip => (
          <span
            key={chip}
            className={cn(
              'inline-flex h-5 items-center rounded-sm border px-1.5 font-mono text-2xs',
              accent
                ? 'border-primary/30 bg-card text-primary'
                : 'border-border/60 bg-card text-muted-foreground'
            )}
          >
            {chip}
          </span>
        ))}
      </div>
      <p className="mt-2.5 text-2xs leading-relaxed text-muted-foreground">{note}</p>
    </motion.div>
  )
}

/** The link between the two boxes — a dashed hop carrying reads one way only. */
function Connector({ label }: { label: string }) {
  return (
    <motion.div
      variants={REVEAL}
      aria-hidden
      className="flex shrink-0 flex-col items-center justify-center gap-1 sm:w-28"
    >
      <div className="flex w-full items-center justify-center gap-1">
        <span className="hidden h-px flex-1 border-t border-dashed border-border sm:block" />
        <ArrowRight className="size-3.5 rotate-90 text-muted-foreground sm:rotate-0" />
        <span className="hidden h-px flex-1 border-t border-dashed border-border sm:block" />
      </div>
      <span className="font-mono text-2xs uppercase tracking-[0.14em] text-muted-foreground">
        {label}
      </span>
    </motion.div>
  )
}

/** One labelled fact under the figure. */
function Fact({ icon, term, detail }: { icon: ReactNode; term: string; detail: string }) {
  return (
    <div className="flex gap-2.5">
      <span className="mt-0.5 shrink-0 text-muted-foreground">{icon}</span>
      <div className="min-w-0">
        <dt className="text-2xs font-medium uppercase tracking-[0.12em] text-muted-foreground">
          {term}
        </dt>
        <dd className="mt-0.5 text-xs leading-relaxed text-foreground">{detail}</dd>
      </div>
    </div>
  )
}
